/**
 * StatRow — fila de 3 mini-tiles escaneables: Racha · Frontera · Hoy.
 * Hoy sólo la usa HomeDesktop (la home móvil pasó a ProgressSubHeader).
 *
 * `useHomeStats()` carga una vez el progreso diario + cobertura + totales y
 * deriva las métricas de la home:
 *   • Racha → `totals.currentStreak` del backend (/progress/totals, tz-aware).
 *     FUENTE CANÓNICA — acá no se computa racha client-side: el viejo
 *     `computeStreakDays` sobre ≤200 sesiones podía divergir del Camino.
 *     `totals === null` = sin dato: las superficies ocultan la métrica en vez
 *     de mostrar un 0 falso.
 *   • Frontera → próxima banda de vocabulario (globalCoverage().frontier).
 *   • Hoy + calendario de la racha → `/progress/daily`, el MISMO endpoint
 *     server-side y tz-aware que alimenta al Camino. Antes se derivaban de
 *     `listSessions` sumando wall-clock, y las dos superficies podían mostrar
 *     números distintos con los mismos datos (ver streakData.ts).
 * No hay meta diaria dura: "Hoy" muestra minutos sin anillo "12/20" (esa
 * métrica no existe en backend — fail-fast, no se inventa).
 */
import { useEffect, useMemo, useState } from 'react';
import { useProgressTotals } from '../../../hooks/useProgressTotals.js';
import {
  browserTimeZone,
  getProgressDaily,
  type DailyProgress,
  type ProgressTotals,
} from '../../../lib/englishApi.js';
import { onProgressChanged } from '../../../lib/progressRefresh.js';
import { fetchCoverage, type Coverage } from '../../../lib/lexiconApi.js';
import { libCoverage, type LibCovTier } from '../../library/libData.js';
import { IconClock, IconLayers } from '../icons.js';
import { StreakFlame } from './StreakFlame.js';
import {
  buildStreakCalendar,
  minutesToday as minutesTodayOf,
  todayCounts,
  type StreakCalendar,
} from './streakData.js';

/**
 * Ventana del heatmap, en días (el tope que acepta `/progress/daily`). El
 * navegador de meses de la hoja se clampea a esta ventana — ver
 * `StreakCalendar.startDate`.
 */
const HEATMAP_DAYS = 400;

export interface HomeStats {
  now: Date;
  /** Racha + acumulados del backend. `null` = sin dato (cargando o error). */
  totals: ProgressTotals | null;
  /** Calendario del heatmap, ya armado desde `/progress/daily`. */
  calendar: StreakCalendar;
  todayDone: boolean;
  minutesToday: number;
  frontier: LibCovTier | null;
  loading: boolean;
}

/** Carga progreso diario + cobertura + totales una vez y deriva las métricas. */
export function useHomeStats(): HomeStats {
  const now = useMemo(() => new Date(), []);
  const [daily, setDaily] = useState<DailyProgress | null>(null);
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [loading, setLoading] = useState(true);
  const { totals, loading: totalsLoading } = useProgressTotals();

  // Etiqueta YYYY-MM-DD de "hoy" en la misma tz con la que se pide el daily,
  // para leer la fila de hoy sin re-derivar el día en otro huso.
  const tz = useMemo(() => browserTimeZone(), []);
  const todayLabel = useMemo(
    () => new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(now),
    [tz, now],
  );

  // Un repaso hecho encima del Inicio puede volver «hoy» un día que cuenta (card #604).
  const [changed, setChanged] = useState(0);
  useEffect(() => onProgressChanged(() => setChanged((n) => n + 1)), []);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [d, cov] = await Promise.all([
          getProgressDaily(tz, HEATMAP_DAYS),
          fetchCoverage(),
        ]);
        if (!alive) return;
        setDaily(d);
        setCoverage(cov);
      } catch {
        // Sin stats: la home degrada a ceros/“—”, no rompe. (No silenciamos un
        // valor mal resuelto: acá un fallo de red simplemente deja la home sin
        // métricas, no enmascara una mala config.)
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [tz, changed]);

  return useMemo(
    () => ({
      now,
      totals,
      calendar: buildStreakCalendar(daily),
      todayDone: todayCounts(daily, todayLabel),
      minutesToday: minutesTodayOf(daily, todayLabel),
      frontier: libCoverage(coverage).frontier,
      loading: loading || totalsLoading,
    }),
    [daily, todayLabel, coverage, now, totals, loading, totalsLoading],
  );
}

interface StatRowProps {
  variant?: 'mobile' | 'desktop';
  /** Racha del backend; `null` = sin dato → se muestra "—". */
  streak: number | null;
  minutesToday: number;
  frontier: LibCovTier | null;
  onStreak: () => void;
  onFrontier: () => void;
  onToday: () => void;
}

export function StatRow({ variant = 'mobile', streak, minutesToday, frontier, onStreak, onFrontier, onToday }: StatRowProps) {
  const cls = variant === 'desktop' ? 'stat-row d-stat-row' : 'stat-row';
  return (
    <div className={cls}>
      {/* Sin dato ('—') el tile va disabled: el sheet que abre está gateado
          por totals — un botón vivo acá era un click muerto (error) o un
          modal que se abría solo cuando el fetch resolvía (carga). */}
      <button type="button" className="stat-tile" onClick={onStreak} disabled={streak === null}>
        <div className="stat-tile-top">
          <span className="stat-tile-ic"><StreakFlame size={14} color="var(--streak)" animate={false} /></span>
          <span className="stat-tile-eyebrow">Racha</span>
        </div>
        <div className="stat-tile-val" style={{ color: 'var(--streak)' }}>
          {streak !== null ? (
            <>{streak}<span className="u">días</span></>
          ) : (
            '—'
          )}
        </div>
      </button>

      <button type="button" className="stat-tile" onClick={onFrontier}>
        <div className="stat-tile-top">
          <span className="stat-tile-ic" style={{ color: frontier?.accent ?? 'var(--text-muted)' }}><IconLayers size={13} /></span>
          <span className="stat-tile-eyebrow">Frontera</span>
        </div>
        {frontier ? (
          <>
            <div className="stat-frontier-row">
              <span className="stat-frontier-tier">{frontier.label}</span>
              <span className="stat-frontier-pct" style={{ color: frontier.accent }}>{frontier.pct}%</span>
            </div>
            <div className="stat-frontier-bar"><i style={{ width: `${frontier.pct}%`, background: frontier.accent }} /></div>
          </>
        ) : (
          <div className="stat-tile-val">—</div>
        )}
      </button>

      <button type="button" className="stat-tile" onClick={onToday}>
        <div className="stat-tile-top">
          <span className="stat-tile-ic" style={{ color: 'var(--cyan)' }}><IconClock size={13} /></span>
          <span className="stat-tile-eyebrow">Hoy</span>
        </div>
        <div className="stat-tile-val">{minutesToday}<span className="u">min</span></div>
      </button>
    </div>
  );
}
